// -----------------------------------------------------------------------------
// Environment.
//
// Read once, validated once, at module load. A missing Firebase key should fail
// loudly on the first paint with a readable message - not two screens later
// inside the SDK as `auth/invalid-api-key`.
//
// Everything here is PUBLIC: it is compiled into the bundle any staff browser
// downloads. The Firebase web config is public by design (it identifies the
// project; it grants nothing). No secret may ever be added to this file.
// -----------------------------------------------------------------------------

function required(name: string, value: string | undefined): string {
  if (!value || value.trim() === '') {
    throw new Error(
      `Missing ${name}. Copy AdminPanel/.env.example to .env.local and fill it in.`,
    );
  }
  return value.trim();
}

const isProduction = import.meta.env.PROD;

/**
 * Required in a production build, optional everywhere else.
 *
 * Used for the App Check site key: shipping the panel to staff with App Check
 * silently off is worth breaking the first paint over, while a developer who has
 * not registered a debug token should still be able to run `npm run dev`.
 */
function requiredInProduction(name: string, value: string | undefined): string {
  const trimmed = value?.trim() ?? '';
  if (isProduction && trimmed === '') {
    throw new Error(
      `Missing ${name}. A production build must ship a reCAPTCHA Enterprise site key - ` +
        'set it in the deploy environment or in AdminPanel/.env.production.',
    );
  }
  return trimmed;
}

export const env = {
  isProduction,

  /** API root WITHOUT the version segment; `apiClient` appends `/api/v1`. */
  apiBaseUrl: required('VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL).replace(/\/+$/, ''),

  firebase: {
    apiKey: required('VITE_FIREBASE_API_KEY', import.meta.env.VITE_FIREBASE_API_KEY),
    authDomain: required('VITE_FIREBASE_AUTH_DOMAIN', import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
    projectId: required('VITE_FIREBASE_PROJECT_ID', import.meta.env.VITE_FIREBASE_PROJECT_ID),

    /**
     * The web app's Firebase App ID.
     *
     * App Check identifies the CALLING APP, not the project, so the SDK puts
     * this straight into its exchange URL:
     * `/v1/projects/{projectId}/apps/{appId}:exchangeRecaptchaEnterpriseToken`.
     * Omit it and that path is built with the literal string `undefined`, the
     * exchange 400s, no attestation token is ever minted, and every API call
     * comes back `APP_CHECK_FAILED` - which is exactly what happened in
     * production. Auth and Storage do not need it, which is why it went missing
     * until App Check was switched on.
     */
    appId: required('VITE_FIREBASE_APP_ID', import.meta.env.VITE_FIREBASE_APP_ID),
    // Without this, `getStorage()` throws `storage/no-default-bucket` the
    // moment anything tries to upload - the SDK has no way to infer it from
    // `projectId` alone. Same value as `Backend/.env`'s `FIREBASE_STORAGE_BUCKET`.
    storageBucket: required(
      'VITE_FIREBASE_STORAGE_BUCKET',
      import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    ),

    /**
     * reCAPTCHA Enterprise site key for App Check.
     *
     * Public like everything else in this block - a site key is embedded in
     * every page that renders the reCAPTCHA widget and attests nothing on its
     * own. The secret half of App Check lives in Google Cloud, and the
     * enforcement half lives in the Firebase console.
     *
     * Empty is a legal value in development only; `src/lib/firebase.ts` then
     * skips App Check for that session.
     */
    appCheckSiteKey: requiredInProduction(
      'VITE_FIREBASE_APP_CHECK_SITE_KEY',
      import.meta.env.VITE_FIREBASE_APP_CHECK_SITE_KEY,
    ),
  },

  /**
   * Mocking is a development affordance and is hard-disabled in a production
   * build. Reading the flag alone would let a mis-set variable ship a build that
   * silently serves fabricated moderation decisions to real staff.
   */
  useMsw: !isProduction && import.meta.env.VITE_USE_MSW === 'true',
} as const;

export const API_V1 = '/api/v1';
